import type { CustomCellRendererProps } from '@mint-lib/ag-grid/react';

import type { GoalCellValue } from '@mint-lib/ag-grid/editors';
import {
  useCurrencyFormatter,
  useNumberFormatter,
  usePercentFormatter,
} from '@mint-lib/formatting-hooks';
import { useTranslation } from '@mint-lib/i18n';
import type { MediaPlan } from '@mint-sdk/api-media-plan';

import type { GoalOptionType } from '../../../api/queries/useGoalsOptionsApi.js';
import { EMPTY_CELL_VALUE } from '../../../consts/grid.consts.jsx';
import { getFormattedValue } from '../../../utils/getFormattedValue.jsx';

export type GoalRendererType = {
  goalOptionsById?: Map<string, GoalOptionType>;
};

export const GoalRenderer = (
  params: CustomCellRendererProps<MediaPlan.MediaData, GoalCellValue> &
    GoalRendererType,
) => {
  const { t } = useTranslation('@myn/planning_v2');
  const { format: formatCurrency } = useCurrencyFormatter();
  const { format: formatNumber } = useNumberFormatter();
  const { format: formatPercent } = usePercentFormatter();

  const formatGoalValue = (value: number, option?: GoalOptionType): string => {
    if (!option) {
      return String(value);
    }

    const maxDecimals = option.decimalPlaces;

    switch (option.valueType) {
      case 'currency': {
        const currency = params.data?.currencyCode;

        if (!currency) {
          return (
            formatNumber(value, { options: { maxDecimals } })?.formatted ??
            String(value)
          );
        }

        return (
          formatCurrency(value, {
            currency,
            minDecimals: maxDecimals,
            maxDecimals,
          })?.formatted ?? String(value)
        );
      }
      case 'percent':
        return (
          formatPercent(value, { maxDecimals })?.formatted ?? String(value)
        );
      case 'decimal':
        return (
          formatNumber(value, { options: { maxDecimals } })?.formatted ??
          String(value)
        );
      default:
        option.valueType satisfies never;
        return String(value);
    }
  };

  const formatterFn = () => {
    const goal = params.value;

    if (!goal?.id) {
      return null;
    }

    const option = params.goalOptionsById?.get(goal.id);
    const label = option?.label ?? goal.id;

    if (goal.value === EMPTY_CELL_VALUE) {
      return label;
    }

    return `${label}: ${formatGoalValue(goal.value, option)}`;
  };

  const formattedValue = getFormattedValue(params, {
    formatterFn,
    placeholderText: t('Insert'),
  });

  return formattedValue;
};
